// frontend/src/components/CaptureAcquisition.js
//
// Marketing-attribution capture (#100). Headless — renders nothing.
//
// Mounted once inside <Router> (App.js), beside ScrollToTop/RedirectHandler.
// On first mount it:
//   1. reads the opaque short codes s (source) and c (campaign) from the
//      landing URL's query string,
//   2. validates each against ^[A-Za-z0-9]{1,8}$ and stores valid ones in
//      sessionStorage ('acq_s' / 'acq_c') FIRST-WRITER-WINS (an existing value
//      is never overwritten — the first tagged arrival of the visit owns it),
//   3. strips s & c from the visible URL via history.replaceState (cleaner
//      shared link; also prevents a re-capture if this ever re-runs),
//   4. fires a single 'utm_visit' event per session through the FIRST-PARTY
//      backend path (never GA4), guarded by a sessionStorage flag.
//
// Steps 1-2 (for every param below) are utils/acquisitionCapture.js
// `captureAcquisitionParams`, which src/index.js ALSO runs synchronously at
// boot, before React exists (#464): a prerendered page is clickable long
// before this effect runs, and a native navigation in that window must not
// lose the codes. Running it again here is idempotent (first writer wins) and
// covers any boot path that skipped index.js's call. Steps 3-4 stay here.
//
// It ALSO captures `src` — the email-CTA tag — on exactly the same terms
// (validate, first-writer-wins into 'acq_src', strip from the URL). What it
// deliberately does NOT do is emit anything for it: `src` rides the
// consent-GATED product event `email_cta_landed`, which AnalyticsContext emits
// once per session, while utm_visit here is consent-INDEPENDENT. Two different
// consent rules, so two different emitters; the payloads never mix.
//
// And `ref` - a friend (partner) code from a partner link
// (`/<page>/?s=g5&ref=<CODE>`): validated (^[A-Za-z0-9-]{3,24}$), upper-cased,
// stored FIRST-WRITER-WINS as 'acq_ref' and stripped from the URL with the
// others. The sign-up form pre-fills its friend-code field from it (and, on a
// link straight to /register?ref=, from the address bar - its first render
// runs BEFORE this effect; see `readLandingFriendCode`). It is NEVER
// emitted anywhere: not in utm_visit, not in any analytics lane. A code in the
// address bar would otherwise reach every analytics surface that reads the
// location, so the strip happens here, in this effect, BEFORE any of them:
//   - this component is the first <Router> child to mount, ahead of
//     <AuthProvider>/<AnalyticsProvider>, and React runs passive effects
//     child-first in tree order - so this effect has already rewritten the URL
//     when AnalyticsProvider's GA4 page_view / Meta PageView effects run;
//   - index.html configures gtag with `send_page_view: false`, so nothing fires
//     on load before React mounts, and every GA4 page_view we send carries the
//     PATH only;
//   - the Meta pixel's own URL allowlist has no `ref` key, so even a missed
//     strip fails closed there.
//
// The codes are treated as OPAQUE strings end to end — the code->surface
// taxonomy lives only in the backend; the frontend never embeds it.
//
// Everything is wrapped so a hostile/absent sessionStorage or a malformed URL
// can never break app boot.

import { useEffect } from "react";
import analytics from "../services/analytics";
import { ACQUISITION_PARAMS, captureAcquisitionParams } from "../utils/acquisitionCapture";

const readCode = (key) => {
  try {
    return sessionStorage.getItem(key) || null;
  } catch (e) {
    return null;
  }
};

const CaptureAcquisition = () => {
  useEffect(() => {
    try {
      // Validate + store, first writer wins (usually already done at boot by
      // src/index.js - this second call then changes nothing).
      captureAcquisitionParams();

      const params = new URLSearchParams(window.location.search);

      // Strip s, c, src & ref from the visible URL regardless of validity — keeps
      // shared links clean and stops a duplicate capture on a re-run. Other
      // params (e.g. ?winback=, ?fb=) are preserved untouched.
      //
      // replaceState edits the browser's URL WITHOUT telling React Router, so
      // the router's `location` still carries the original search string for
      // this render — which is fine here because nothing downstream reads it,
      // and it is why the stored value, not the URL, is the source of truth.
      if (ACQUISITION_PARAMS.some((name) => params.has(name))) {
        ACQUISITION_PARAMS.forEach((name) => params.delete(name));
        const qs = params.toString();
        const newUrl =
          window.location.pathname +
          (qs ? `?${qs}` : "") +
          window.location.hash;
        try {
          window.history.replaceState(window.history.state, "", newUrl);
        } catch (e) {
          /* replaceState blocked (e.g. sandbox) — non-fatal */
        }
      }

      // One first-party utm_visit per session, only when this visit actually
      // carries an acquisition. sendToBackend() bypasses GA4 entirely.
      const s = readCode("acq_s");
      const c = readCode("acq_c");
      if (s || c) {
        let alreadySent = false;
        try {
          alreadySent = Boolean(sessionStorage.getItem("acq_visit_sent"));
        } catch (e) {
          /* ignore */
        }
        if (!alreadySent) {
          try {
            sessionStorage.setItem("acq_visit_sent", "1");
          } catch (e) {
            /* ignore */
          }
          const eventParams = {};
          if (s) eventParams.s = s;
          if (c) eventParams.c = c;
          analytics.sendToBackend("utm_visit", eventParams);
        }
      }
    } catch (e) {
      // Attribution capture must never break the app.
    }
    // Run once on mount — captures the landing URL for this visit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

export default CaptureAcquisition;
